// Helpers for validating the origin of incoming window `message` events.
//
// A `message` listener receives events from ANY window/frame/tab unless the
// origin is checked. Handlers that act on message data (submitting prompts,
// flipping connection state, mutating editor state) must therefore verify
// `event.origin` before trusting the payload.

import { baseURL } from "@/utils/appsettings";

// Trusted Joylo domains that host generated-app previews / sidecars / the API.
const TRUSTED_JOYLO_SUFFIXES = [
  ".joylo.dev",
  ".joylo.ai",
  ".joylo.io",
  ".joylo.org",
];

function hostnameOf(origin: string | undefined | null): string | null {
  if (!origin) return null;
  try {
    return new URL(origin).hostname;
  } catch {
    return null;
  }
}

/**
 * True if the origin is a Joylo-controlled domain (preview/deployment/API) or
 * localhost during local development.
 */
export function isTrustedJoyloOrigin(origin: string | undefined | null): boolean {
  const hostname = hostnameOf(origin);
  if (!hostname) return false;
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  return TRUSTED_JOYLO_SUFFIXES.some(
    (suffix) => hostname === suffix.slice(1) || hostname.endsWith(suffix),
  );
}

/**
 * Origins that host the preview iframe (the generated app). Used by handlers
 * receiving messages from the preview so unrelated tabs/pages can't inject.
 */
export function isTrustedPreviewOrigin(origin: string | undefined | null): boolean {
  return isTrustedJoyloOrigin(origin);
}

/**
 * True if the message came from a trusted backend origin. The Stripe OAuth
 * callback page is served by the backend API (a different origin than the SPA),
 * so this accepts the app's own origin, the configured API origin, and any
 * Joylo-controlled domain — but not arbitrary external pages.
 */
export function isTrustedBackendOrigin(origin: string | undefined | null): boolean {
  if (!origin) return false;
  try {
    if (origin === window.location.origin) return true;
  } catch {
    /* ignore */
  }
  try {
    if (baseURL) {
      const apiOrigin = new URL(baseURL, window.location.origin).origin;
      if (origin === apiOrigin) return true;
    }
  } catch {
    /* ignore */
  }
  return isTrustedJoyloOrigin(origin);
}
